import React from "react"
import {useTranslation} from "react-i18next"
import {
  FloatingFocusManager,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStatus,
} from "@floating-ui/react"
import {XMarkIcon} from "@heroicons/react/20/solid"
import {twMerge} from "tailwind-merge"

type TProps = {
  title: React.ReactNode
  /** Rendered beside the title, outside the dialog's accessible name. */
  titleAside?: React.ReactNode
  /** Rendered under the title, still inside the header. */
  headerContent?: React.ReactNode
  /** The panel's width class; the lead drawer is narrower than the Update center. */
  widthClassName?: string
  onClose: () => void
  children: React.ReactNode
}

/**
 * Right-hand dialog panel for the dashboard. Mount it only while open: focus moves in on mount,
 * Escape and a backdrop click close it, and focus returns to the opener on unmount.
 * It slides in on mount; closing unmounts it at once, so there is no closing animation.
 */
export const Drawer: React.FC<TProps> = ({
  title,
  titleAside,
  headerContent,
  widthClassName = "w-[540px]",
  onClose,
  children,
}) => {
  const {t} = useTranslation()
  const titleId = React.useId()

  const {refs, context} = useFloating({
    open: true,
    onOpenChange: open => {
      if (!open) {
        onClose()
      }
    },
  })
  const dismiss = useDismiss(context, {outsidePress: false})
  const {getFloatingProps} = useInteractions([dismiss])
  const {status} = useTransitionStatus(context, {duration: {open: 300}})
  const isOpen = status === "open"

  return (
    <FloatingPortal>
      <div
        className={twMerge(
          "fixed inset-0 z-40 bg-cr-slate-90/30 transition-opacity duration-300 ease-out motion-reduce:transition-none",
          isOpen ? "opacity-100" : "opacity-0"
        )}
        data-testid={"drawer backdrop"}
        onClick={onClose}
      />
      {/* Focus starts on the panel itself, so opening never pops up a control's tooltip on its own. */}
      <FloatingFocusManager context={context} initialFocus={refs.floating}>
        <div
          ref={refs.setFloating}
          role={"dialog"}
          aria-modal={"true"}
          aria-labelledby={titleId}
          data-status={status}
          className={twMerge(
            "fixed top-[var(--header-height)] right-0 bottom-0 z-50 flex max-w-full flex-col bg-cr-white shadow-[-8px_0_28px_color-mix(in_srgb,var(--color-cr-blue-dark)_14%,transparent)] pixel-units max-sm:w-full",
            widthClassName,
            "transition duration-300 ease-out motion-reduce:transition-none",
            isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-90"
          )}
          {...getFloatingProps()}
        >
          <div className={"flex flex-col gap-3.5 border-b border-cr-slate-2 px-6 py-5"}>
            <div className={"flex items-start justify-between gap-3"}>
              <div className={"flex min-w-0 items-center gap-1"}>
                <h2 id={titleId} className={"text-lg font-bold tracking-[-0.3px] text-cr-slate-90"}>
                  {title}
                </h2>
                {titleAside}
              </div>
              <button
                type={"button"}
                className={
                  "flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-cr-slate-6 text-cr-slate-60 transition-colors hover:text-cr-slate-90"
                }
                onClick={onClose}
              >
                <span className={"sr-only"}>{t("T_Close")}</span>
                <XMarkIcon className={"size-4"} aria-hidden={"true"} />
              </button>
            </div>
            {headerContent}
          </div>
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}
